/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import { memo, SyntheticEvent, useState, useCallback, Fragment, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import cx from 'classnames';

import { isEmpty } from 'helpers/utils';
import useVirtualizerAutoScroll from 'hooks/componentHooks/useVirtualizerAutoScroll';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';

import classes from './deltaTable.module.scss';
import { useDeltaTable } from './DeltaTableProvider';
import { TableRowsProps } from './interface';

const noop = (): null => null;

const TableRows = <T,>({
  rowClass,
  expandedRowGlobalClass,
  onRowClick,
  showFooter,
  expandableRowsComponent,
  allowAccordionExpand,
  footerRowClass,
  onRowMouseEnter,
  onRowMouseLeave,
  parentRef,
  virtualOverscan = 5,
  rowHeight,
  showBody = true,
  autoScroll,
}: TableRowsProps<T>) => {
  const { sortOrder, columns, data } = useDeltaTable();
  const rowRef = useRef<Array<HTMLTableRowElement | null>>([]);

  const clickable = typeof onRowClick === 'function';
  const originalDataLength = data?.length ?? 0;

  const [tableData, setTableData] = useState<T[]>(() => data as T[]);
  const [expandedIndex, setExpandedIndex] = useState<number | undefined>(undefined);
  const [expandedIndexName, setExpandedIndexName] = useState<string | undefined>(
    undefined
  );

  const handleResetExpansion = () => {
    setExpandedIndex(undefined);
    setExpandedIndexName(undefined);
  };

  const handleUpdates = useCallback(() => {
    setTableData(data as T[]);
  }, [data]);

  useDidUpdateEffect(() => {
    handleUpdates();
  }, [handleUpdates, sortOrder, originalDataLength]);

  const onExpandClick = (idx: number, dataIndex: string) => () => {
    if (!expandableRowsComponent) {
      throw new Error('expandableRowsComponent is required');
    }
    if (idx === expandedIndex) {
      setExpandedIndex(undefined);
      setExpandedIndexName(undefined);
      return;
    }
    setExpandedIndex(idx);
    setExpandedIndexName(dataIndex);
  };

  const rowVirtualizer = useVirtualizer({
    count: tableData?.length,
    getScrollElement: () => parentRef?.current,
    estimateSize: () => rowHeight || 60,
    overscan: virtualOverscan || 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  useVirtualizerAutoScroll({
    rowVirtualizer,
    parentRef,
    tableData,
    autoScroll,
  });

  return (<>
    {showBody && (
      <tbody data-palette="TableRows">
        {paddingTop > 0 && (
          <tr data-palette="TableRows">
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td style={{ height: `${paddingTop}px` }} />
          </tr>
        )}
        {virtualRows.map(virtualRow => {
          const row = tableData[virtualRow.index];
          const rowIndex = virtualRow.index;
          const disableRowAction =
            !allowAccordionExpand &&
            !isEmpty(expandedIndexName) &&
            rowIndex !== expandedIndex;
          return (
            (<Fragment key={virtualRow.key}>
              <tr
                ref={el => {
                  rowRef.current[rowIndex] = el;
                  if (!rowHeight) {
                    rowVirtualizer.measureElement(el);
                  }
                }}
                data-row-expanded={rowIndex === expandedIndex}
                data-disabled={disableRowAction}
                className={cx(classes.positionTest, {
                  [`${rowClass}`]: rowClass,
                  [`${classes.disabled}`]: disableRowAction,
                  [classes.expandedRow]: rowIndex === expandedIndex,
                })}
                data-index={rowIndex}
              >
                {columns.map((column, colIndex) => {
                  const {
                    fixed,
                    align,
                    hide,
                    render,
                    renderView,
                    allowAnyRowExpandCollapse,
                    disableRowClick,
                  } = column;
                  const dataIndex = column.dataIndex || column.key;
                  const Cell = `${fixed ? 'th' : 'td'}` as keyof JSX.IntrinsicElements;

                  const disableCell =
                    !allowAccordionExpand &&
                    !allowAnyRowExpandCollapse &&
                    rowIndex === expandedIndex &&
                    dataIndex !== expandedIndexName;
                  const isCellExpanded =
                    rowIndex === expandedIndex && dataIndex === expandedIndexName;

                  return hide ? null : (
                    <Cell
                      tabIndex={-1}
                      data-testid={`cell-${colIndex}`}
                      role="gridcell"
                      key={`cell-${colIndex}`}
                      data-index={dataIndex}
                      disabled={disableCell}
                      className={cx({
                        [`${classes.alignLeft}`]: align === 'left',
                        [`${classes.alignCenter}`]: align === 'center',
                        [`${classes.alignRight}`]: align === 'right',
                        [`${classes.clickable}`]: clickable && !disableRowClick,
                        [`${classes.disabled}`]: disableCell,
                        [`${classes.fixed}`]: fixed,
                        [`${column.bodyCellClassName}`]: column.bodyCellClassName,
                      })}
                      onClick={
                        clickable
                          ? (event: SyntheticEvent<Element>) =>
                              // onRowClick && onRowClick(event, row, dataIndex)
                              onRowClick &&
                              !disableRowClick &&
                              onRowClick({
                                event,
                                rowData: row,
                                rowIndex,
                                onExpandClick: onExpandClick(rowIndex, dataIndex),
                                rowElement: rowRef.current[rowIndex],
                              })
                          : noop
                      }
                      onMouseEnter={(event: SyntheticEvent<Element>) =>
                        onRowMouseEnter?.({
                          event,
                          rowData: row,
                          rowIndex,
                          onExpandClick: onExpandClick(rowIndex, dataIndex),
                          rowElement: rowRef.current[rowIndex],
                        })
                      }
                      onMouseLeave={(event: SyntheticEvent<Element>) =>
                        onRowMouseLeave?.({
                          event,
                          rowData: row,
                          rowIndex,
                          onExpandClick: onExpandClick(rowIndex, dataIndex),
                          rowElement: rowRef.current[rowIndex],
                        })
                      }
                    >
                      {renderView &&
                        renderView({
                          // @ts-ignore
                          displayValue: row[column.key],
                          rowData: row,
                          rowIndex,
                          onExpandClick: onExpandClick(rowIndex, dataIndex),
                          isExpanded: isCellExpanded,
                          dataIndex,
                          colIndex,
                        })}
                      {renderView
                        ? null
                        : render
                        ? render(
                            // @ts-ignore
                            row[column.key],
                            row,
                            onExpandClick(rowIndex, dataIndex),
                            isCellExpanded,
                            dataIndex,
                            rowIndex,
                            colIndex
                          )
                        : // @ts-ignore
                          row[column.key]}
                    </Cell>
                  );
                })}
              </tr>
              {expandableRowsComponent && expandedIndex === rowIndex && (
                <tr
                  className={cx(classes.expanded, 'expand', {
                    [`${expandedRowGlobalClass}`]: expandedRowGlobalClass,
                  })}
                  data-palette="TableRows">
                  <td colSpan={columns.length}>
                    {expandableRowsComponent({
                      expandedRowData: row,
                      // @ts-ignore
                      expandedRowIndexName: expandedIndexName,
                      handleResetExpansion,
                    })}
                  </td>
                </tr>
              )}
            </Fragment>)
          );
        })}
        {/* Do not remove these 2 rows. live update of orderbook fails */}
        {paddingBottom > 0 && (
          <tr data-palette="TableRows">
            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <td style={{ height: `${paddingBottom}px` }} />
          </tr>
        )}
      </tbody>
    )}
    {showFooter && (
      <tfoot data-palette="TableRows">
        <tr className={cx({ [footerRowClass as string]: footerRowClass })}>
          {columns.length
            ? columns.map((footerCol, idx) => {
                const { fixed, align, columnFooterComponent, hide, footerCellClass } =
                  footerCol;
                // eslint-disable-next-line no-undef
                const Cell = `${fixed ? 'th' : 'td'}` as keyof JSX.IntrinsicElements;
                return hide ? null : (
                  <Cell
                    tabIndex={-1}
                    data-footer
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    className={cx({
                      [`${classes.alignLeft}`]: align === 'left',
                      [`${classes.alignCenter}`]: align === 'center',
                      [`${classes.alignRight}`]: align === 'right',
                      [`${classes.fixed}`]: fixed,
                      [footerCellClass as string]: footerCellClass,
                    })}
                  >
                    {columnFooterComponent ? columnFooterComponent(data as T[]) : null}
                  </Cell>
                );
              })
            : null}
        </tr>
      </tfoot>
    )}
  </>);
};

export default memo(TableRows);
